/**
 * Shared types for the Onboarding wizard.
 * These match Spec 1's ColumnsResponse shape exactly (camelCase, same keys/nullability).
 */

export interface ColumnInfo {
  columnName: string;
  dataType: string;
  isNullable: boolean;
  ordinalPosition: number;
  distinctValues: number | null;
  sampleValues: string[] | null;
}

export interface ColumnsResponse {
  tableName: string;
  schema: string | null;
  columns: ColumnInfo[];
}

/**
 * /api/onboarding/connect request.
 * host/port/database now come from the AWS secret, so the user only supplies
 * the secret ARN (clusterIdentifier/region/connectionType were removed).
 *
 * NOTE: request bodies use snake_case keys. powertools 3.6.0 fails to bind a
 * request body when a required Pydantic field carries an alias (returns 422
 * loc=["query","body"]), so the backend models are snake_case with no alias and
 * the wire contract matches (same pattern as the config-generator endpoints).
 */
export interface RedshiftConnectionRequest {
  secret_arn: string;
}

/** Matches Spec 1's ColumnsRequest (Redshift introspection path). */
export interface ColumnsRequest extends RedshiftConnectionRequest {
  schema_name: string;
  table_name: string;
}

/** /api/onboarding/connect response */
export interface ConnectResponse {
  connected: boolean;
  schemas: string[];
}

/** /api/onboarding/check-procedure request */
export interface CheckProcedureRequest extends RedshiftConnectionRequest {
  procedure_name: string;
  // Optional. The universal stored procedure lives in a fixed schema (public on
  // FDA) that is independent of the user's data-table schema, so the SP check
  // must NOT be pinned to the data-table schema. Omit it to search all schemas.
  schema_name?: string;
}

/** /api/onboarding/check-procedure response */
export interface CheckProcedureResponse {
  // True only when the entry-point procedure AND all its runtime dependencies
  // are installed. The universal SP CALLs a logging SP unqualified in its
  // success/error paths, so a cluster with the report SP but not the logger
  // would still fail at runtime — exists reflects the whole runnable set.
  exists: boolean;
  procedureName: string;
  // Schema(s) where the entry-point procedure was found (empty when it is absent).
  foundSchemas: string[];
  // Required procedures (entry-point + dependencies) that are NOT installed.
  // Empty when exists is true; lets the UI name exactly what is missing.
  missingProcedures: string[];
}

/** /api/onboarding/install-procedures request */
export interface InstallProceduresRequest extends RedshiftConnectionRequest {
  // Optional; the backend defaults to the v1 SP (sp_bluey_common_report) and
  // installs it plus its runtime dependencies. Not interpolated into SQL — the
  // installed DDL is vendored — but identifier-validated server-side.
  procedure_name?: string;
}

/** /api/onboarding/install-procedures response */
export interface InstallProceduresResponse {
  // Procedures installed, in dependency-first order (the logger before the
  // report proc). Lets the UI confirm exactly what was created.
  installedProcedures: string[];
  // Schema the procedures were installed into (always "public" for v1).
  schema: string;
  // Result of the post-install re-check: true when the whole required set is now
  // present, so the UI can flip its indicator green without a second round-trip.
  verified: boolean;
}

// ─── Catalog write (metric catalog tables) ──────────────────────────────
//
// Bluey's stored procedure resolves every requested metric against two Redshift
// TABLES (see catalogRows.ts), so those tables must exist and be populated
// before a generated tool can answer anything. This endpoint creates them and
// inserts the rows. Wire body is snake_case, matching the backend field names.

/** One base-metrics catalog row on the wire. */
export interface CatalogBaseMetricPayload {
  metric_key: string;
  expr_sql: string;
  agg: string;
  filter_condition: string | null;
}

/** One derived-metrics catalog row on the wire. */
export interface CatalogDerivedMetricPayload {
  derived_metric_key: string;
  base_metrics_csv: string;
  expr_sql: string;
  description: string;
}

/** /api/onboarding/write-catalogs request */
export interface WriteCatalogsRequest extends RedshiftConnectionRequest {
  schema_name: string;
  base_table: string;
  derived_table: string;
  base_metrics: CatalogBaseMetricPayload[];
  derived_metrics: CatalogDerivedMetricPayload[];
}

/** /api/onboarding/write-catalogs response */
export interface WriteCatalogsResponse {
  /** Fully-qualified names of tables this call created. */
  tablesCreated: string[];
  /**
   * Tables that ALREADY existed and were empty, so they were populated without
   * being created. A pre-existing table with rows is refused (409) instead, so
   * anything listed here was safe to fill — but it is worth telling the user a
   * table with that name was already on the cluster.
   */
  tablesReused?: string[];
  /** Rows inserted per catalog. */
  rowsWritten: { base: number; derived: number };
}

/**
 * Error code returned (with HTTP 409) when either target table already holds
 * rows. The write is create-only: nothing is inserted into EITHER table in that
 * case, so the user's existing catalog is never clobbered.
 */
export const CATALOG_NOT_EMPTY_CODE = 'CATALOG_NOT_EMPTY';

// ─── Config tester (advisory schema validation) ─────────────────────────
//
// Runs the already-assembled YAML through the backend's Bluey-config schema
// validator on the Review step. Advisory only — it never gates Finish. Fast and
// synchronous (schema validation, not an LLM job): a validation failure comes
// back as HTTP 200 with `approved: false` and field-level errors; only
// network/other failures surface as non-2xx → OnboardingApiError. The wire body
// is snake_case (like writeCatalogs), so it is passed through unmapped.

/** /api/onboarding/test-config request (already snake_case; passed through). */
export interface ConfigTestRequest {
  config_yaml: string;
  // Optional. When present (and a valid ARN), the backend ALSO runs a read-only
  // Redshift dry-run that resolves every base metric's SQL against the config's
  // source_table_name — catching a mistyped/nonexistent column that schema
  // validation alone cannot (a bad column name is still a valid string). The
  // resulting errors arrive under `base_metrics.<key>` in `errors`, and a
  // cluster that could not be reached arrives as a `warnings` entry (a skip, not
  // a failure). Omit it for the original schema-only check.
  secret_arn?: string;
}

/** One field-level schema error. `loc` is the dotted path into the config. */
export interface ConfigFieldError {
  loc: string;
  message: string;
}

/** /api/onboarding/test-config response (camelCase on the wire). */
export interface ConfigTestResponse {
  approved: boolean;
  toolName?: string;
  toolType?: string;
  errors: ConfigFieldError[];
  warnings: string[];
}

/** /api/onboarding/tables request */
export interface TablesRequest extends RedshiftConnectionRequest {
  schema_name: string;
}

/** Individual table info from /tables response */
export interface TableInfo {
  tableName: string;
  tableType: string;
  estimatedRowCount: number | null;
}

/** /api/onboarding/tables response */
export interface TablesResponse {
  tables: TableInfo[];
}

/** /api/onboarding/parse-ddl request */
export interface DdlParseRequest {
  ddl: string;
}

// ─────────────────────────────────────────────────────────────────────────
// /api/onboarding/infer-metrics (Spec 2) — LLM metric inference.
//
// These are the LOGICAL request/response types the frontend reasons about.
// The actual on-the-wire REQUEST is snake_case (see inferMetrics() in
// onboardingApi.ts, which maps these → the backend model's snake_case field
// NAMES). The backend binds those via `populate_by_name=True` — the same way
// the Spec-1 connect/tables/columns endpoints send snake_case field names
// rather than the camelCase aliases. The RESPONSE comes back camelCase (the
// backend response models alias out), so the response interfaces below match
// the wire shape directly.
// ─────────────────────────────────────────────────────────────────────────

/** One column's metadata + sample values, forwarded to the LLM. */
export interface ColumnSamplePayload {
  columnName: string;
  dataType: string;
  isNullable: boolean;
  distinctValues: number | null;
  sampleValues: string[] | null;
}

/** The source table (schema + name + columns) the LLM proposes metrics for. */
export interface SourceTablePayload {
  schema: string | null;
  tableName: string;
  columns: ColumnSamplePayload[];
}

/** /api/onboarding/infer-metrics request (logical shape; sent as snake_case). */
export interface InferMetricsRequest {
  toolName: string;
  category: string;
  sourceTable: SourceTablePayload;
  /** Optional free-text context; backend caps at 4000 chars. */
  businessContext?: string;
  existingMetricsCatalog?: unknown[];
}

/** A base metric the LLM proposes. `rationale` explains the choice. */
export interface BaseMetricProposal {
  metricKey: string;
  exprSql: string;
  agg: AggFunction;
  filterCondition: string | null;
  /**
   * Whether this base is independently user-facing (`true`) or a dependency-only
   * helper consumed by a derived metric (`false`). The backend's metric-inference
   * dependency graph sets this (see `metric_inference_service` Step E); a helper
   * base is kept in the catalog so its derived consumer resolves, but should NOT
   * be exposed as a standalone queryable metric. Optional/absent is treated as
   * `true` for backward compatibility, matching the backend's `publish: bool = True`.
   */
  publish?: boolean;
  rationale: string | null;
}

/** A derived metric the LLM proposes. */
export interface DerivedMetricProposal {
  derivedMetricKey: string;
  baseMetricsCsv: string;
  exprSql: string;
  description: string;
}

/** /api/onboarding/infer-metrics response (camelCase on the wire). */
export interface InferMetricsResponse {
  baseMetrics: BaseMetricProposal[];
  derivedMetrics: DerivedMetricProposal[];
  warnings: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Async LLM inference (Spec 3.5) — submit → poll job mechanism.
//
// Both /infer-metrics and /infer-parameters now have `-async` siblings that
// return a jobId in <1s (the real Bedrock work runs in a background Lambda with
// no API-Gateway 29s ceiling). The frontend polls GET /infer-jobs/{jobId} until
// the job is COMPLETED/FAILED. `result` is free-form: for a METRICS job it holds
// an InferMetricsResponse shape, for a PARAMETERS job an InferParametersResponse
// shape — the calling advisor knows which based on which submit it issued, so it
// casts accordingly.
// ─────────────────────────────────────────────────────────────────────────

export type JobType = 'METRICS' | 'PARAMETERS' | 'DESCRIPTION';
export type JobStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

/** Response to a POST /infer-*-async submit (camelCase on the wire). */
export interface InferJobSubmitResponse {
  jobId: string;
  status: JobStatus;
  jobType: JobType;
}

/** Response to a GET /infer-jobs/{jobId} poll (camelCase on the wire). */
export interface InferJobStatusResponse {
  jobId: string;
  jobType: JobType;
  status: JobStatus;
  /** Free-form when COMPLETED; the advisor casts to its own response type. */
  result?: unknown;
  error?: string | null;
  code?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Week 4 contract (LOCKED) — auto-inference + config generation, client-side.
// Both Week 4 builders code against these. Do not change without coordinating.
// ─────────────────────────────────────────────────────────────────────────

export type AggFunction = 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT' | 'COUNT_DISTINCT';
export type ParamType = 'string' | 'number' | 'boolean' | 'array';

export interface BaseMetricDraft {
  metricKey: string;
  exprSql: string;
  agg: AggFunction;
  filterCondition: string | null;
  /**
   * Whether this base is exposed as a standalone queryable metric (`true`) or
   * kept only as a dependency for a derived metric (`false`). Drives whether
   * `buildCatalogRows` synthesizes a `ROUND(<key>,2)` passthrough for it. Optional
   * with an implicit `true` default so every pre-existing construction site (and
   * hand-added rows) keeps compiling and stays exposed unless deliberately hidden.
   */
  publish?: boolean;
}

export interface DerivedMetricDraft {
  derivedMetricKey: string;
  baseMetricsCsv: string;
  exprSql: string;
  description: string;
}

export interface FilterCandidate {
  columnName: string;
  enumValues: string[] | null;
}

export interface InferenceResult {
  toolName: string;
  category: string;
  dateColumns: string[];
  metricCandidates: BaseMetricDraft[];
  filterCandidates: FilterCandidate[];
  parameterTypeMap: Record<string, ParamType>;
}

export interface OnboardingConfig {
  toolName: string;
  toolType: 'UNIVERSAL_STORED_PROCEDURE';
  category: string;
  /**
   * Free-text dataset purpose written by the user on the Schema Import step.
   * Captured client-side now; later fed as `businessContext` to the LLM metric
   * (Spec 2) and description (Spec 6) inference. Optional — never gates Next.
   */
  businessContext: string;
  /**
   * Optional per-column notes, keyed by columnName (e.g. "value_usd" ->
   * "revenue in USD"). Improves later LLM metric/description suggestions.
   * Intentionally NOT part of ColumnInfo/ColumnsResponse (which must stay
   * byte-identical to Spec 1) — notes are wizard state only.
   */
  columnNotes: Record<string, string>;
  sourceTableName: string;
  storedProcedure: string;
  /**
   * Schema that houses the two metric-catalog tables Bluey resolves metrics
   * against (`meta_<tool>_metrics_catalog` / `…_derived_metrics_catalog`).
   * Defaults to `bluey_input` — the convention on every real FDA catalog — and
   * is editable on the Review step in case the target cluster differs. Feeds
   * both the YAML `*_metrics_catalog` fields and the catalog-write call.
   */
  catalogSchema: string;
  database: {
    clusterIdentifier: string;
    database: string;
    secretArns: { test: string; beta: string; live: string };
  };
  baseMetrics: BaseMetricDraft[];
  derivedMetrics: DerivedMetricDraft[];
  /**
   * Proposed/edited input_schema parameters (Spec 3). Each row's `required`
   * boolean is the single source of truth for the YAML `required` block — it is
   * derived from the rows at serialization time, not stored separately, so a
   * rename/remove/toggle can never desync from a parallel list.
   */
  parameters: ParameterDraft[];
  /**
   * The YAML top-level `description` block — the multi-section markdown document
   * that teaches the Bluey LLM polisher when/how to use the tool (Spec 6).
   * Proposed by the LLM on the Description step and freely editable; optional,
   * never gates Next.
   */
  toolDescription: string;
}

// ─────────────────────────────────────────────────────────────────────────
// MetricAdvisor contract (LOCKED) — swappable metric-suggestion seam.
// heuristicAdvisor (client-side, now) and a future agentCoreAdvisor (Bedrock
// AgentCore, when a metric-agent runtime ARN exists) both implement this.
// `suggest` is async so both share the exact same signature (drop-in swap).
// ─────────────────────────────────────────────────────────────────────────

export interface MetricSuggestionInput {
  columns: ColumnInfo[];
  businessContext: string;
  columnNotes: Record<string, string>;
}

export interface MetricSuggestion {
  baseMetrics: BaseMetricDraft[];
  derivedMetrics: DerivedMetricDraft[];
  warnings: string[];
}

export interface MetricAdvisor {
  suggest(input: MetricSuggestionInput): Promise<MetricSuggestion>;
}

// ─────────────────────────────────────────────────────────────────────────
// /api/onboarding/infer-parameters (Spec 3) — LLM parameter inference.
//
// Same wire convention as Spec 2: the LOGICAL request types below are
// camelCase; inferParameters() in onboardingApi.ts maps them to the backend
// model's snake_case field NAMES on the wire. The RESPONSE comes back camelCase
// (inputSchema/required/warnings) and matches the interfaces directly.
//
// `inputSchema` is a free-form dict[paramName -> param spec dict]. Its exact
// shape varies per parameter type (string vs array<array<string>>, enum,
// items, examples, default, …), so it is typed loosely and the wizard carries
// the opaque keys it doesn't model on ParameterDraft.raw for later YAML
// round-trip.
// ─────────────────────────────────────────────────────────────────────────

/** /api/onboarding/infer-parameters request (logical shape; sent as snake_case). */
export interface InferParametersRequest {
  toolName: string;
  category: string;
  sourceTable: SourceTablePayload;
  /** The user-confirmed metric set from the Metrics step (Spec 2 output). */
  confirmedMetrics: {
    baseMetrics: BaseMetricDraft[];
    derivedMetrics: DerivedMetricDraft[];
  };
  /** Optional free-text context; backend caps at 4000 chars. */
  businessContext?: string;
  /** When true, the LLM adds a `method` parameter (filter/YoY/MoM). */
  supportsPeriodComparison: boolean;
}

/** A single proposed parameter spec (free-form per parameter type). */
export type ParameterProposal = Record<string, unknown>;

/** /api/onboarding/infer-parameters response (camelCase on the wire). */
export interface InferParametersResponse {
  inputSchema: Record<string, ParameterProposal>;
  required: string[];
  warnings: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// ParameterAdvisor contract — swappable parameter-suggestion seam, mirroring
// MetricAdvisor. heuristicParameterAdvisor (client-side, network-free) and the
// LLM-backed createLlmParameterAdvisor both implement this. `suggest` is async
// so the two are drop-in swappable.
// ─────────────────────────────────────────────────────────────────────────

/**
 * One editable parameter row in the Parameters step. `raw` carries opaque LLM
 * fields (items, examples, …) that the table doesn't render so they survive
 * for the later YAML round-trip.
 */
export interface ParameterDraft {
  parameterName: string;
  type: ParamType;
  description: string;
  required: boolean;
  enumValues: string[] | null;
  defaultValue: string | null;
  raw?: Record<string, unknown>;
}

export interface ParameterSuggestionInput {
  columns: ColumnInfo[];
  businessContext: string;
  columnNotes: Record<string, string>;
  confirmedBaseMetrics: BaseMetricDraft[];
  confirmedDerivedMetrics: DerivedMetricDraft[];
  supportsPeriodComparison: boolean;
}

export interface ParameterSuggestion {
  parameters: ParameterDraft[];
  warnings: string[];
}

export interface ParameterAdvisor {
  suggest(input: ParameterSuggestionInput): Promise<ParameterSuggestion>;
}

// ─────────────────────────────────────────────────────────────────────────
// /api/onboarding/generate-description (Spec 6) — LLM description generation.
//
// Same wire convention as Spec 2/3: the LOGICAL request is camelCase; the
// submit client (submitGenerateDescription in onboardingApi.ts) maps it to the
// backend model's snake_case field NAMES on the wire (tool_name, category,
// business_context, confirmed_metrics.{base_metrics,derived_metrics},
// parameter_names, supports_period_comparison). The v1 wizard has no UI to
// capture sibling_tools/forbidden_topics, so those are omitted (the backend
// defaults them to []). The RESPONSE comes back camelCase
// (description/lineCount/sectionsIncluded/warnings). Description jobs reuse the
// generic async submit/poll (InferJobSubmitResponse/InferJobStatusResponse) —
// jobType DESCRIPTION.
// ─────────────────────────────────────────────────────────────────────────

/** /api/onboarding/generate-description request (logical shape; sent as snake_case). */
export interface GenerateDescriptionRequest {
  toolName: string;
  category: string;
  /** The user-confirmed metric set from the Metrics step (Spec 2 output). */
  confirmedMetrics: {
    baseMetrics: BaseMetricDraft[];
    derivedMetrics: DerivedMetricDraft[];
  };
  /** The confirmed parameter NAMES from the Parameters step (Spec 3 output). */
  parameterNames: string[];
  /** Optional free-text context; backend caps at 4000 chars. */
  businessContext?: string;
  /** When true, the description documents period-comparison (YoY/MoM) behavior. */
  supportsPeriodComparison: boolean;
}

/** /api/onboarding/generate-description response (camelCase on the wire). */
export interface GenerateDescriptionResponse {
  description: string;
  lineCount: number;
  sectionsIncluded: string[];
  warnings: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// DescriptionAdvisor contract — swappable description-suggestion seam,
// mirroring MetricAdvisor / ParameterAdvisor. heuristicDescriptionAdvisor
// (network-free fallback) and the LLM-backed createLlmDescriptionAdvisor both
// implement this. `suggest` is async so the two are drop-in swappable. The
// input carries the full ParameterDraft[]; the advisor maps them to
// parameterNames for the wire request.
// ─────────────────────────────────────────────────────────────────────────

export interface DescriptionSuggestionInput {
  columns: ColumnInfo[];
  businessContext: string;
  columnNotes: Record<string, string>;
  confirmedBaseMetrics: BaseMetricDraft[];
  confirmedDerivedMetrics: DerivedMetricDraft[];
  parameters: ParameterDraft[];
}

export interface DescriptionSuggestion {
  description: string;
  sections?: string[];
  warnings: string[];
}

export interface DescriptionAdvisor {
  suggest(input: DescriptionSuggestionInput): Promise<DescriptionSuggestion>;
}
